/**
 * Map view for /arena/find — Leaflet + OpenStreetMap.
 * Uses gym.lat/gym.lng if present; otherwise falls back to a city centroid.
 */
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";

// Lightweight city centroid table — covers ~30 Indian cities the app primarily targets
const CITY_CENTROIDS = {
  "Mumbai": [19.0760, 72.8777],
  "Delhi": [28.6139, 77.2090],
  "New Delhi": [28.6139, 77.2090],
  "Bangalore": [12.9716, 77.5946],
  "Bengaluru": [12.9716, 77.5946],
  "Hyderabad": [17.3850, 78.4867],
  "Chennai": [13.0827, 80.2707],
  "Kolkata": [22.5726, 88.3639],
  "Pune": [18.5204, 73.8567],
  "Ahmedabad": [23.0225, 72.5714],
  "Surat": [21.1702, 72.8311],
  "Jaipur": [26.9124, 75.7873],
  "Lucknow": [26.8467, 80.9462],
  "Kanpur": [26.4499, 80.3319],
  "Nagpur": [21.1458, 79.0882],
  "Indore": [22.7196, 75.8577],
  "Thane": [19.2183, 72.9781],
  "Bhopal": [23.2599, 77.4126],
  "Visakhapatnam": [17.6868, 83.2185],
  "Patna": [25.5941, 85.1376],
  "Vadodara": [22.3072, 73.1812],
  "Ghaziabad": [28.6692, 77.4538],
  "Ludhiana": [30.9010, 75.8573],
  "Agra": [27.1767, 78.0081],
  "Nashik": [19.9975, 73.7898],
  "Faridabad": [28.4089, 77.3178],
  "Meerut": [28.9845, 77.7064],
  "Rajkot": [22.3039, 70.8022],
  "Kochi": [9.9312, 76.2673],
  "Goa": [15.2993, 74.1240],
  "Chandigarh": [30.7333, 76.7794],
  "Gurugram": [28.4595, 77.0266],
  "Gurgaon": [28.4595, 77.0266],
  "Noida": [28.5355, 77.3910],
};

// Custom gold-shield marker icon (no external image)
const arenaIcon = L.divIcon({
  className: "",
  html: '<div style="width:30px;height:30px;border-radius:50%;background:#F5C518;border:3px solid #0C0C0B;box-shadow:0 4px 10px rgba(0,0,0,0.30);display:grid;place-items:center;color:#0C0C0B;font-weight:900;font-family:Barlow Condensed,sans-serif;font-size:14px;">A</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export default function ArenaMapView({ gyms, city }) {
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [pinned, setPinned] = useState([]);

  useEffect(() => {
    const list = (gyms || []).map((g) => {
      let lat = g.lat;
      let lng = g.lng;
      if (lat == null || lng == null) {
        const cc = CITY_CENTROIDS[g.city] || CITY_CENTROIDS[(city || "").trim()];
        if (cc) {
          // Add a tiny deterministic jitter so multiple gyms in same city don't overlap
          const seed = (g.gym_id || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
          lat = cc[0] + ((seed % 100) - 50) * 0.0008;
          lng = cc[1] + (((seed >> 3) % 100) - 50) * 0.0008;
        }
      }
      return { gym: g, pos: lat != null && lng != null ? [lat, lng] : null };
    });
    const valid = list.filter((x) => x.pos);
    setPinned(valid);
    setPoints(valid.map((x) => x.pos));
  }, [gyms, city]);

  const center = points[0] || CITY_CENTROIDS[(city || "").trim()] || [20.5937, 78.9629]; // India centroid

  if (!gyms?.length) {
    return (
      <div data-testid="arena-map-empty" style={{ padding: 40, textAlign: "center", background: "var(--bg-surface, #E4E2DE)", borderRadius: 14 }}>
        <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800 }}>No gyms in {city || "this area"}</div>
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>Refer your gym to earn 500 FitPoints when they activate.</p>
      </div>
    );
  }

  return (
    <div data-testid="arena-map" style={{ height: 480, borderRadius: 14, overflow: "hidden", border: "1px solid var(--border, rgba(12,12,11,0.10))" }}>
      <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {pinned.map(({ gym, pos }) => (
          <Marker key={gym.gym_id} position={pos} icon={arenaIcon}>
            <Popup>
              <div style={{ fontFamily: "Barlow", minWidth: 180 }}>
                <div style={{ fontFamily: "Barlow Condensed", fontSize: 16, fontWeight: 800 }}>{gym.gym_name}</div>
                <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{gym.city}{gym.address ? ` · ${gym.address.slice(0, 50)}` : ""}</div>
                <button onClick={() => navigate(`/arena/${gym.gym_code}`)} data-testid={`map-popup-${gym.gym_id}`}
                  style={{ marginTop: 8, width: "100%", padding: "6px 10px", background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
                  View gym →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
